/**
 * Course payment callables for onboarding checkout
 *
 * Wraps the `runCoursePaymentCheckout` HTTPS callable with a typed success/error result for the
 * BNPL checkout screen.
 *
 * References:
 * - `docs/PF-4846/dependencies-and-references.md`
 */

import { FirebaseError } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';
import type { RunCoursePaymentCheckoutRequest, RunCoursePaymentCheckoutResponse } from '@physiofit-digital/physiofit_types';
import { getCoursePaymentFunctions } from '../../../firebaseConfig';

export type OnboardingCoursePaymentErrorKind =
  | 'risk-check-rejected'
  | 'invalid-argument'
  | 'permission-denied'
  | 'unauthenticated'
  | 'not-found'
  | 'network'
  | 'unknown';

export type OnboardingCoursePaymentError = {
  kind: OnboardingCoursePaymentErrorKind;
  message: string;
  code?: string;
};

export type OnboardingCoursePaymentResult<T> =
  | {
      ok: true;
      data: T;
    }
  | {
      ok: false;
      error: OnboardingCoursePaymentError;
    };

/**
 * Maps Firebase callable errors to onboarding payment error kinds for UI copy.
 *
 * @param {unknown} error Error from `httpsCallable` rejection.
 * @return {Object} Normalized error with `kind`, `message`, and optional `code`.
 */
const mapCallableError = (error: unknown): OnboardingCoursePaymentError => {
  if (error instanceof FirebaseError) {
    const code = error.code;
    if (code === 'functions/failed-precondition') {
      return {
        kind: 'risk-check-rejected',
        code,
        message: error.message,
      };
    }
    if (code === 'functions/invalid-argument') {
      return { kind: 'invalid-argument', code, message: error.message };
    }
    if (code === 'functions/permission-denied') {
      return { kind: 'permission-denied', code, message: error.message };
    }
    if (code === 'functions/unauthenticated') {
      return { kind: 'unauthenticated', code, message: error.message };
    }
    if (code === 'functions/not-found') {
      return { kind: 'not-found', code, message: error.message };
    }
    if (code === 'functions/unavailable') {
      return { kind: 'network', code, message: error.message };
    }
    return { kind: 'unknown', code, message: error.message };
  }

  if (error instanceof Error) {
    return {
      kind: 'unknown',
      message: error.message,
    };
  }

  return {
    kind: 'unknown',
    message: 'Unexpected callable error',
  };
};

/**
 * Runs the BNPL provider checkout in a single callable: initialise, verify server-side that the
 * DATA_PRIVACY text the patient ticked still matches the provider's current document, accept it,
 * and authorize.
 *
 * ERS-25 replaced the previous two-call client sequence (`initCoursePaymentCheckout` then
 * `authorizeCoursePayment`, sequenced client-side). The consent comparison now happens on the
 * server (`normalizeConsentText`, whitespace-collapsing) instead of a client-side `.trim()` a
 * direct caller could simply skip, and only the required document is accepted — the optional
 * MARKETING entry is filled by the documented provider workaround server-side, not blanket-accepted
 * here as the old client-side sequence did.
 *
 * @param {Object} req `RunCoursePaymentCheckoutRequest` payload.
 * @return {Object} Discriminated result: `ok: false` for technical failures, else the outcome union.
 */
export const startBnplCoursePayment = async (
  req: RunCoursePaymentCheckoutRequest,
): Promise<OnboardingCoursePaymentResult<RunCoursePaymentCheckoutResponse>> => {
  const callable = httpsCallable<RunCoursePaymentCheckoutRequest, RunCoursePaymentCheckoutResponse>(
    getCoursePaymentFunctions(),
    'runCoursePaymentCheckout',
  );

  try {
    const { data } = await callable(req);
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: mapCallableError(error) };
  }
};
